import { useEffect, useMemo, useState } from 'react';

const useIsXl = () => {
  const [xl, setXl] = useState(() => window.matchMedia('(min-width: 1280px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1280px)');
    const h = (e) => setXl(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return xl;
};
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FolderOpen, Trash2, Archive, Boxes, Globe, Clock, X } from 'lucide-react';
import { toast } from 'sonner';
import HUDPageHeader from '../components/HUDPageHeader';
import HUDStatCard from '../components/HUDStatCard';
import NewProjectWizard from '../components/NewProjectWizard';
import { ProjectsTable } from '../components/projects/ProjectsTable';
import { ProjectRecapPanel } from '../components/projects/ProjectRecapPanel';
import { BulkActionsBar, RenameProjectDialog, MoveToFolderDialog, ConfirmDeleteDialog } from '../components/projects/ProjectsActions';
import { useProjectsHubStore, filterProjects, isActive, TRASH_DAYS } from '../stores/useProjectsHubStore';

const VIEW_TITLES = {
  all: ['Mes Projets', 'Votre espace personnel : programmes, maquettes et rendus en un coup d\u2019œil.'],
  published: ['Projets publiés', 'Visibles côté clients.'],
  archived: ['Projets archivés', 'Rangés hors de la liste principale, restaurables à tout moment.'],
  trash: ['Corbeille', `Les projets y restent ${TRASH_DAYS} jours avant suppression définitive.`],
};

const EmptyState = ({ view, folder, search, onCreate }) => {
  const cfg = search ? [Search, `Aucun résultat pour « ${search} »`, 'Essayez un autre nom de projet ou de ville.']
    : view === 'trash' ? [Trash2, 'La corbeille est vide', 'Les projets supprimés apparaîtront ici pendant 30 jours.']
    : view === 'archived' ? [Archive, 'Aucun projet archivé', 'Archivez un projet terminé depuis son menu « ⋯ ».']
      : folder ? [FolderOpen, `Le dossier « ${folder.name} » est vide`, 'Glissez un projet depuis la liste sur ce dossier pour le ranger.']
        : [FolderOpen, 'Aucun projet', 'Créez votre premier projet pour commencer.'];
  const [Icon, title, hint] = cfg;
  return (
    <div className="text-center py-20 rounded-2xl border border-dashed border-[var(--c-border-strong)] bg-[var(--c-surface-0)]" data-testid="projects-empty-state">
      <Icon className="w-9 h-9 mx-auto mb-4 text-[var(--c-text-faint)]" strokeWidth={1.25} />
      <h3 className="text-[16px] font-semibold text-[var(--c-text)]" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
      <p className="mt-1.5 text-[13px] text-[var(--c-text-muted)]">{hint}</p>
      {view === 'all' && !folder && !search && (
        <button type="button" onClick={onCreate} className="hud-btn hud-btn-primary hud-btn-md mt-6" data-testid="empty-create-project-btn"><Plus className="w-4 h-4" />Créer un projet</button>
      )}
    </div>
  );
};

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { projects, folders, loading, filter, search, setSearch, selectedId, fetchAll, organize } = useProjectsHubStore();
  const [showCreate, setShowCreate] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [moving, setMoving] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const isXl = useIsXl();

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const visible = useMemo(() => filterProjects(projects, filter, search), [projects, filter, search]);
  const selected = projects.find((p) => p.project_id === selectedId) || null;
  const folder = filter.startsWith('folder:') ? folders.find((f) => f.folder_id === filter.slice(7)) : null;
  const [title, subtitle] = folder ? [folder.name, 'Dossier de projets.'] : (VIEW_TITLES[filter] || VIEW_TITLES.all);

  const active = projects.filter(isActive);
  const stats = {
    total: active.length,
    published: active.filter((p) => p.is_public).length,
    lots: active.reduce((a, p) => a + (p.lots_count || 0), 0),
    inProgress: active.filter((p) => p.health_status === 'amber').length,
  };

  const act = async (ids, action) => {
    if (action === 'delete') { setDeleting(ids); return; }
    try {
      await organize(ids, action);
      const msgs = { archive: 'Projet(s) archivé(s)', unarchive: 'Projet(s) désarchivé(s)', trash: `Placé dans la corbeille · suppression dans ${TRASH_DAYS} j`, restore: 'Projet(s) restauré(s)' };
      toast.success(msgs[action] || 'Fait');
    } catch { toast.error('Action impossible'); }
  };

  const confirmDelete = async () => {
    const ids = deleting;
    setDeleting(null);
    try { const r = await organize(ids, 'delete'); toast.success(`${r.affected} projet(s) supprimé(s) définitivement`); } catch { toast.error('Suppression impossible'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="projects-loading">
        <div className="w-7 h-7 border-2 border-[var(--c-gold)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-hud-page data-testid="projects-page" className="min-h-full flex flex-col">
      <HUDPageHeader
        module="Espace personnel"
        breadcrumb={[{ label: 'Hub', to: '/studio/dashboard' }, { label: 'Projets' }]}
        title={title}
        subtitle={subtitle}
        actions={(
          <>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--c-text-faint)]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un projet, une ville…"
                data-testid="projects-search-input"
                className="h-10 w-[220px] sm:w-[280px] pl-9 pr-8 rounded-xl border border-[var(--c-border)] bg-[var(--c-surface-0)] text-[13px] text-[var(--c-text)] placeholder:text-[var(--c-text-faint)] outline-none focus:border-[var(--c-gold)] focus:ring-2 focus:ring-[var(--c-gold-soft)] transition"
              />
              {search && <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--c-text-faint)] hover:text-[var(--c-text)]" aria-label="Effacer" data-testid="projects-search-clear"><X className="w-3.5 h-3.5" /></button>}
            </div>
            <button type="button" onClick={() => setShowCreate(true)} className="hud-btn hud-btn-primary hud-btn-md" data-testid="create-project-btn">
              <Plus className="w-4 h-4" />Nouveau projet
            </button>
          </>
        )}
      />

      <div className="flex-1 flex min-h-0">
        <div className={`flex-1 min-w-0 px-5 sm:px-8 pt-2 pb-7 space-y-6 transition-[padding] duration-300 ${selected ? 'xl:pr-6' : ''}`}>
          {filter === 'all' && !search && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="projects-stats-row">
              <HUDStatCard testid="stat-total-projects" icon={FolderOpen} label="Projets" value={stats.total} hint="Actifs, hors archives" />
              <HUDStatCard testid="stat-in-progress-projects" icon={Clock} label="En cours" value={stats.inProgress} hint="Maquettes ou plans en production" />
              <HUDStatCard testid="stat-published-projects" icon={Globe} label="Publiés" value={stats.published} hint="Visibles côté clients" />
              <HUDStatCard testid="stat-total-lots" icon={Boxes} label="Lots" value={stats.lots} hint="Tous projets confondus" />
            </div>
          )}

          {filter === 'trash' && visible.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50 text-[12.5px] text-amber-800" data-testid="trash-retention-banner">
              <Trash2 className="w-4 h-4 shrink-0" />Les projets de la corbeille sont supprimés définitivement {TRASH_DAYS} jours après leur mise à la corbeille.
            </div>
          )}

          {visible.length === 0 ? (
            <EmptyState view={filter} folder={folder} search={search.trim()} onCreate={() => setShowCreate(true)} />
          ) : (
            <>
              <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--c-text-faint)] -mb-4 px-1" data-testid="projects-count">{visible.length} projet{visible.length > 1 ? 's' : ''}{search ? ` pour « ${search} »` : ''}</p>
              <ProjectsTable
                projects={visible}
                onRename={setRenaming}
                onMove={setMoving}
                onAct={act}
                onOpen={(p) => navigate(`/projects/${p.project_id}`)}
              />
            </>
          )}
          <BulkActionsBar view={filter} onMove={setMoving} onAct={act} />
        </div>

        <div
          className={`hidden xl:block shrink-0 sticky top-0 self-start h-[calc(100vh-56px)] overflow-hidden transition-[width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${selected ? 'w-[440px] 2xl:w-[480px]' : 'w-0'}`}
          data-testid="recap-panel-slot"
        >
          <div className="w-[440px] 2xl:w-[480px] h-full">{selected && isXl && <ProjectRecapPanel project={selected} onMove={setMoving} onAct={act} />}</div>
        </div>
      </div>

      {selected && !isXl && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="recap-panel-overlay">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => useProjectsHubStore.getState().closePanel()} />
          <div className="relative w-full max-w-[460px] h-full"><ProjectRecapPanel project={selected} onMove={setMoving} onAct={act} /></div>
        </div>
      )}

      <NewProjectWizard open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) fetchAll(); }} />
      {renaming && <RenameProjectDialog project={renaming} onClose={(ok, err) => { setRenaming(null); if (ok) toast.success('Projet modifié'); if (err) toast.error(err); }} />}
      {moving && <MoveToFolderDialog ids={moving} onClose={(ok, err) => { setMoving(null); if (ok) toast.success('Projet(s) déplacé(s)'); if (err) toast.error(err); }} />}
      {deleting && <ConfirmDeleteDialog ids={deleting} onClose={() => setDeleting(null)} onConfirm={confirmDelete} />}
    </div>
  );
};

export default ProjectsPage;
